/**
 * Applies constructable stylesheet overrides to Shadow DOM hosts inside
 * .wd-custom-shopify-form (e.g. Shopify Forms app embed): RTL, Alef, theme button
 * colors, RTL border clipping, Callbox hidden field UI, and window.setShopifyFormSid().
 */
(function () {
  if (typeof CSSStyleSheet === 'undefined') return;

  var patched = new WeakSet();

  // The custom CSS we want to inject into the Shadow DOM
  var customStyles = `
    :host {
      /*
       * Match wd-contact-us-form + footer: labels/copy inherit .footer 18px;
       * .field__input in base.css uses 1.3rem + letter-spacing 0.04rem.
       */
      --forms-body-font: normal 400 18px 'Alef', sans-serif !important;
      --forms-heading-font: normal 400 16px 'Alef', sans-serif !important;
      --container-text-alignment: right !important;
      --inline-form-alignment: right !important;
      /* If the app reads this token, it may reduce clipping without extra rules */
      --forms-padding-left: 6px;
    }
    
    * {
      font-family: 'Alef', sans-serif !important;
    }

    form {
      font-size: 18px !important;
      padding-left: 6px !important;
      box-sizing: border-box !important;
    }

    input,
    textarea,
    select,
    button {
      font-size: 1.3rem !important;
      letter-spacing: 0.04rem !important;
    }

    input,
    textarea {
      box-sizing: border-box !important;
      max-width: 100% !important;
    }

    /* Core RTL setup — stable selectors only (no hashed app build class names) */
    form,
    input,
    textarea,
    button,
    p,
    h2,
    span,
    :host > * {
      direction: rtl !important;
      text-align: right !important;
    }

    /* Floating labels fix for RTL — 18px matches .footer / .wd-contact-form__checkbox-label */
    label {
      font-size: 18px !important;
      font-family: 'Alef', sans-serif !important;
      direction: rtl !important;
      text-align: right !important;
      right: 15px !important;
      left: auto !important;
      transform-origin: top right !important;
    }

    /*
     * Callbox tracking field: id is literally "custom#sid" — use attribute selector, not #id.
     * Hide field and its direct parent so layout keeps no gap (:has needs Safari 15.4+).
     */
    [id="custom#sid"] {
      display: none !important;
      visibility: hidden !important;
      position: absolute !important;
      width: 0 !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: none !important;
      opacity: 0 !important;
      pointer-events: none !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
    }
    *:has(> [id="custom#sid"]) {
      display: none !important;
      height: 0 !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
      visibility: hidden !important;
      border: none !important;
    }

    /* Match theme global solid button tokens (see assets/base.css :root) */
    form button[type="submit"] {
      background-color: rgb(var(--color-button-background)) !important;
      color: rgb(var(--color-button-text)) !important;
      transition: opacity 0.3s ease !important;
    }

    form button[type="submit"]:hover {
      opacity: 0.8 !important;
    }
  `;

  /**
   * Footer accordion title (section-footer.css / wd-footer): 16px, 400, 0.2em, uppercase, black.
   * Icon: 22×22 like .wd-footer-block__title-icon — URL from [data-form-heading-icon] on .wd-custom-shopify-form.
   */
  function getFormHeadingIconUrl(element) {
    var wrap =
      element &&
      element.closest &&
      element.closest('.wd-custom-shopify-form');
    if (!wrap || !wrap.dataset) return '';
    return String(wrap.dataset.formHeadingIcon || '').trim();
  }

  function buildFormHeaderStyles(iconUrl) {
    var hasIcon = iconUrl.length > 0;
    var afterRule = hasIcon
      ? `
    section[class*="formHeader"] h2::after {
      content: '' !important;
      display: block !important;
      width: 22px !important;
      height: 22px !important;
      flex-shrink: 0 !important;
      background-image: url(${JSON.stringify(iconUrl)}) !important;
      background-size: contain !important;
      background-repeat: no-repeat !important;
      background-position: center !important;
    }
    `
      : '';

    return (
      `
    section[class*="formHeader"] {
      direction: rtl !important;
      text-align: right !important;
      margin: 0 !important;
    }
    section[class*="formHeader"] h2,
    section[class*="formHeader"] h2[class*="textHeading"] {
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
      gap: 0.5rem !important;
      font-size: 16px !important;
      font-weight: 400 !important;
      letter-spacing: 0.2em !important;
      text-transform: uppercase !important;
      color: #000 !important;
      margin: 0 !important;
      font-family: 'Alef', sans-serif !important;
    }
    section[class*="formHeader"] div[class*="textBody"]:empty {
      display: none !important;
    }
    ` + afterRule
    );
  }

  function adoptCustomSheet(element) {
    if (!element || !element.shadowRoot) return;
    var root = element.shadowRoot;
    
    if (patched.has(root)) return;
    
    try {
      var sheet = new CSSStyleSheet();
      var iconUrl = getFormHeadingIconUrl(element);
      sheet.replaceSync(customStyles + buildFormHeaderStyles(iconUrl));
      
      // CRITICAL FIX: Append the new sheet to existing ones instead of overwriting them
      var existingSheets = Array.from(root.adoptedStyleSheets || []);
      root.adoptedStyleSheets = [...existingSheets, sheet];
      
      patched.add(root);
    } catch (e) {
      /* Safari / cross-origin: adoptedStyleSheets may throw */
    }
  }

  function scanContainer(container) {
    if (!container) return;
    
    // Check if the container itself has a shadow root
    if (container.shadowRoot) {
      adoptCustomSheet(container);
    }
    
    // Scan all descendants
    container.querySelectorAll('*').forEach(function (el) {
      if (el.shadowRoot) {
        adoptCustomSheet(el);
      }
    });
  }

  function scan() {
    document.querySelectorAll('.wd-custom-shopify-form').forEach(scanContainer);
  }

  var debounceTimer;
  function debouncedScan() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(scan, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Observe the DOM for dynamically injected forms
  var observer = new MutationObserver(debouncedScan);
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();

/** Callbox: set hidden Shopify Form field `id="custom#sid"` inside footer embed shadow trees. */
(function () {
  var SID_ATTR = '[id="custom#sid"]';

  function dispatchReactFriendly(input, value) {
    var opts = { bubbles: true, composed: true };
    input.dispatchEvent(new Event('change', opts));
    try {
      input.dispatchEvent(
        new InputEvent('input', {
          bubbles: true,
          composed: true,
          data: value,
          inputType: 'insertFromPaste',
        })
      );
    } catch (e) {
      /* InputEvent unsupported — fallback to basic Event */
      input.dispatchEvent(new Event('input', opts));
    }
  }

  function walkShadowTree(shadowRoot, visitor) {
    if (!shadowRoot) return;
    visitor(shadowRoot);
    shadowRoot.querySelectorAll('*').forEach(function (node) {
      if (node.shadowRoot) walkShadowTree(node.shadowRoot, visitor);
    });
  }

  window.setShopifyFormSid = function (value) {
    var str = value == null ? '' : String(value);
    document.querySelectorAll('.wd-custom-shopify-form').forEach(function (wrap) {
      function enterFromLight(rootEl) {
        rootEl.querySelectorAll('*').forEach(function (node) {
          if (!node.shadowRoot) return;
          walkShadowTree(node.shadowRoot, function (sr) {
            var input = sr.querySelector(SID_ATTR);
            if (!input) return;
            input.value = str;
            dispatchReactFriendly(input, str);
          });
        });
        if (rootEl.shadowRoot) {
          walkShadowTree(rootEl.shadowRoot, function (sr) {
            var input = sr.querySelector(SID_ATTR);
            if (!input) return;
            input.value = str;
            dispatchReactFriendly(input, str);
          });
        }
      }
      enterFromLight(wrap);
    });
  };
})();